import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Sparkles, Rocket, Loader2, CheckCircle2, Mic, Square } from "lucide-react";
import { Layout } from "../components/Layout";
import { api, API } from "../lib/api";
import { buildProfileContext } from "../lib/profile";
import { getSuggestions, getGreeting } from "../lib/businessContext";
import { journeyT } from "../lib/journey";
import { useApp } from "../context/AppContext";
import { useLang } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Markdown } from "../components/Markdown";

export default function CommandCenter() {
  const { data, launchPlan, pendingCommand, setPendingCommand } = useApp();
  const { lang, t } = useLang();
  const J = journeyT(lang);
  const nav = useNavigate();
  const [messages, setMessages] = useState([{ role: "ai", text: getGreeting(data, lang) }]);
  const suggestions = getSuggestions(data, lang);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [launchingId, setLaunchingId] = useState(null);
  const [listening, setListening] = useState(false);
  const sessionId = useRef(`sess_${Date.now()}`);
  const scrollRef = useRef(null);
  const recognitionRef = useRef(null);
  const abortRef = useRef(null);
  const inactivityRef = useRef(null);
  const mountedRef = useRef(true);
  const consumedCmd = useRef(false);

  // Cleanup on unmount: abort any in-flight AI stream, clear timers, stop the mic.
  // This guarantees leaving Command Center never leaves a hung request or fires
  // setState on an unmounted component.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
      if (inactivityRef.current) clearTimeout(inactivityRef.current);
      try { recognitionRef.current?.stop(); } catch { /* noop */ }
    };
  }, []);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages]);
  // Keep the initial greeting in the active language until the first message is sent.
  useEffect(() => {
    setMessages((m) => (m.length === 1 && m[0].role === "ai" ? [{ role: "ai", text: getGreeting(data, lang) }] : m));
  }, [lang, data]);

  const LANG_MAP = { fr: "fr-FR", en: "en-US", es: "es-ES" };
  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("La saisie vocale n'est pas supportée par ce navigateur. Essayez Chrome ou Edge."); return; }
    if (listening) { recognitionRef.current?.stop(); return; }
    const rec = new SR();
    rec.lang = LANG_MAP[localStorage.getItem("lb_lang") || "fr"] || "fr-FR";
    rec.interimResults = true;
    rec.continuous = false;
    let finalText = "";
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += t; else interim += t;
      }
      setInput((finalText + interim).trim());
    };
    rec.onerror = () => { setListening(false); toast.error("Micro indisponible. Vérifiez les autorisations du navigateur."); };
    rec.onend = () => { setListening(false); };
    recognitionRef.current = rec;
    setListening(true);
    toast.info("Parlez maintenant… 🎙️");
    rec.start();
  };

  const stop = () => {
    abortRef.current?.abort();
  };

  const send = async (text) => {
    const q = text || input;
    if (!q.trim() || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }, { role: "ai", text: "", streaming: true, source: q }]);
    setLoading(true);

    let acc = "";
    // One streaming attempt. Mutates `acc`. Throws on network/HTTP error or abort.
    const attempt = async () => {
      acc = "";
      const controller = new AbortController();
      abortRef.current = controller;
      const armWatchdog = () => {
        if (inactivityRef.current) clearTimeout(inactivityRef.current);
        inactivityRef.current = setTimeout(() => controller.abort("timeout"), 30000);
      };
      armWatchdog();
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST", headers: { "Content-Type": "application/json", "X-Lang": localStorage.getItem("lb_lang") || "fr" }, signal: controller.signal,
        body: JSON.stringify({ message: q, session_id: sessionId.current, profile_context: buildProfileContext(data) }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        armWatchdog();
        acc += decoder.decode(value, { stream: true });
        if (!mountedRef.current) return;
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "ai", text: acc, streaming: true, plan: true, source: q }; return c; });
      }
    };

    try {
      try {
        await attempt();
      } catch (e1) {
        const stopped1 = e1?.name === "AbortError" || abortRef.current?.signal.aborted;
        // Silent single retry only on transient failure (e.g. 502/network) with nothing received.
        if (stopped1 || acc !== "" || !mountedRef.current) throw e1;
        await new Promise((r) => setTimeout(r, 900));
        if (!mountedRef.current) return;
        await attempt();
      }
      if (!mountedRef.current) return;
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "ai", text: acc, plan: true, source: q }; return c; });
    } catch (e) {
      if (!mountedRef.current) return;
      const stopped = e?.name === "AbortError" || abortRef.current?.signal.aborted;
      setMessages((m) => {
        const c = [...m];
        if (stopped && acc.trim()) {
          c[c.length - 1] = { role: "ai", text: acc, plan: acc.length > 120, source: q };
        } else if (stopped) {
          c[c.length - 1] = { role: "ai", text: "⏹️ Génération arrêtée." };
        } else {
          c[c.length - 1] = { role: "ai", text: "Une erreur est survenue. Réessayez." };
        }
        return c;
      });
    } finally {
      if (inactivityRef.current) { clearTimeout(inactivityRef.current); inactivityRef.current = null; }
      abortRef.current = null;
      if (mountedRef.current) setLoading(false);
    }
  };

  const doLaunch = async (idx, msg) => {
    if (launchingId !== null) return;
    setLaunchingId(idx);
    try {
      const { data: plan } = await api.post("/ai/plan", { message: msg.source || msg.text.slice(0, 200), context: msg.text, profile_context: buildProfileContext(data) }, { timeout: 120000 });
      const { ok, duplicate } = launchPlan({ ...plan, key: msg.source || msg.text.slice(0, 200) });
      if (duplicate) {
        toast("Plan déjà actif", { description: "Ce plan a déjà été lancé.", action: { label: "Voir le plan", onClick: () => nav("/app/dashboard") } });
      } else if (ok) {
        const nMiss = (plan.missions || []).length;
        const nCal = (plan.missions || []).filter((m) => typeof m.day_offset === "number").length;
        // "Wow" confirmation: show exactly what LOCALBOOST really prepared (truthful).
        const confirmText = [
          `## ✅ ${J.planReady}`,
          `- ${J.missionsCreated(nMiss)}`,
          nCal ? `- ${J.addedToCalendar(nCal)}` : null,
          `- ${J.trackingOn}`,
          ``,
          `_${J.willTrack}_`,
        ].filter(Boolean).join("\n");
        setMessages((m) => {
          const c = [...m];
          c[idx] = { ...c[idx], launched: true };
          return [...c, { role: "ai", text: confirmText, confirmation: true }];
        });
        toast.success(`${nMiss} missions créées !`, { description: "Retrouvez-les dans votre tableau de bord.", action: { label: "Voir le plan", onClick: () => nav("/app/dashboard") } });
      }
    } catch {
      toast.error("Impossible de générer le plan. Réessayez.");
    } finally { setLaunchingId(null); }
  };

  // Guided journey: when arriving from the Dashboard "Act with AI" CTA, auto-send the
  // priority query so a plan is produced immediately — the merchant never re-explains.
  useEffect(() => {
    if (consumedCmd.current || !pendingCommand) return;
    consumedCmd.current = true;
    const q = pendingCommand;
    setPendingCommand(null);
    send(q);
  }, [pendingCommand]); // eslint-disable-line

  return (
    <Layout>
      <div className="max-w-4xl mx-auto flex flex-col h-[calc(100dvh-11rem)] lg:h-[calc(100vh-8rem)]">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center text-neon"><Sparkles size={22} /></div>
          <div><h1 className="font-display font-black text-2xl">{t("title_command")}</h1><p className="text-sm text-[hsl(var(--muted-foreground))]">Votre copilote commercial · Claude Sonnet 4.6</p></div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-4 pr-1 pb-4" data-testid="chat-messages">
          {messages.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${m.role === "user" ? "bg-primary text-white" : "glass border border-white/10"}`}>
                {m.role === "user" ? (
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.text}</p>
                ) : (
                  <div className="text-sm leading-relaxed">
                    <Markdown>{m.text}</Markdown>
                    {m.streaming && <span className="inline-block w-1.5 h-4 bg-neon ml-0.5 animate-pulse-glow align-middle" />}
                  </div>
                )}
                {m.role === "ai" && m.plan && !m.streaming && m.text.length > 120 && (
                  m.launched ? (
                    <Button data-testid="view-plan-btn" onClick={() => nav("/app/dashboard")} variant="outline" className="mt-3 rounded-full h-9 text-xs border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                      <CheckCircle2 size={14} className="mr-1" /> Plan actif — Voir le plan
                    </Button>
                  ) : (
                    <Button data-testid="launch-plan-btn" onClick={() => doLaunch(i, m)} disabled={launchingId !== null}
                      className="mt-3 rounded-full h-9 text-xs bg-gradient-to-r from-primary to-neon text-white lb-glow-btn">
                      {launchingId === i ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Rocket size={14} className="mr-1" />} LANCER LE PLAN
                    </Button>
                  )
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {messages.length <= 1 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            {suggestions.map((s, i) => (
              <button key={i} data-testid={`suggestion-${i}`} onClick={() => send(s)}
                className="text-left text-xs p-3 rounded-xl glass border border-white/10 hover:border-primary/40 transition-colors text-[hsl(var(--muted-foreground))] hover:text-white">
                {s}
              </button>
            ))}
          </div>
        )}

        <div className="flex gap-2 glass border border-white/10 rounded-2xl p-2">
          <Button data-testid="chat-voice" onClick={toggleVoice} title="Dicter à la voix"
            className={`rounded-xl h-11 w-11 p-0 shrink-0 ${listening ? "bg-red-500/90 text-white animate-pulse" : "bg-white/5 text-[hsl(var(--muted-foreground))] hover:text-white"}`}>
            <Mic size={18} />
          </Button>
          <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Décrivez votre besoin…" className="flex-1 bg-transparent px-3 outline-none text-sm" />
          {loading ? (
            <Button data-testid="chat-stop" onClick={stop} title="Arrêter la génération" className="rounded-xl bg-red-500/90 hover:bg-red-500 text-white h-11 w-11 p-0 shrink-0">
              <Square size={16} className="fill-current" />
            </Button>
          ) : (
            <Button data-testid="chat-send" onClick={() => send()} className="rounded-xl bg-gradient-to-r from-primary to-neon text-white lb-glow-btn h-11 w-11 p-0 shrink-0">
              <Send size={18} />
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
}
